import { ReactNode, useState } from "react";
import { cva, VariantProps } from "class-variance-authority";
import { cn } from "@/utils/classes";

/* Variants */
const variants = cva("card relative bg-base-100 border-2 border-base-300 rounded-2xl h-full cursor-pointer hover:bg-base-200 transition-all duration-300 ease-out", {
  variants: {
    selected: {
      true: "card-active border-primary bg-primary hover:bg-primary",
      false: "",
    },
    size: {
      small: "w-auto max-w-60 rounded-lg",
      large: "min-w-72 max-w-60",
      xlarge: "min-w-86 max-w-90 rounded-lg",
      auto: "w-auto",
    },
  },
  defaultVariants: {
    size: "large",
  },
});

/* Prop Types */
interface CardProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof variants> {
  imageUrl?: string;
  selected?: boolean;
  winner?: boolean;
  hideWinnerBorder?: boolean;
  children?: ReactNode;
  onClick?: () => void;
  imgFit?: "cover" | "contain";
}

/*
 * Card Component
 */
export const Card = ({ imageUrl, selected, size, imgFit = "cover", children, onClick, winner, hideWinnerBorder, className }: CardProps) => {
  /* State */
  // The renderer can be slow to generate a thumbnail even at this size - keep a skeleton in its
  // place until the image has actually loaded, instead of a blank gap.
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  /* Render */
  return (
    <div
      className={cn("relative border-none rounded-2xl", {
        "bg-linear-to-r from-[#4E499C] to-[#F2318A] p-0.5": winner && !hideWinnerBorder,
        "rounded-lg": size === "small" || size === "xlarge",
      })}
    >
      <div className={cn(variants({ selected, size }), className)} onClick={onClick}>
        {/* Card Container */}
        <div className={cn("h-full rounded-2xl pb-1", { "rounded-lg pb-0 overflow-hidden": size === "small", "rounded-lg": size === "xlarge" })}>
          {/* Card Image */}
          {imageUrl ? (
            <div
              className={cn("relative overflow-hidden w-full bg-base-200 h-[184px] rounded-t-2xl", {
                "max-h-28": size === "small",
                "rounded-t-lg": size === "small" || size === "xlarge",
              })}
            >
              {!isImageLoaded && <div className="skeleton absolute inset-0" />}
              <img
                src={imageUrl}
                alt="thumbnail"
                crossOrigin="use-credentials"
                draggable={false}
                onLoad={() => setIsImageLoaded(true)}
                className={cn("absolute inset-0 size-full transition-opacity duration-200", isImageLoaded ? "opacity-100" : "opacity-0", {
                  "object-cover": imgFit === "cover",
                  "object-contain": imgFit === "contain",
                })}
              />
            </div>
          ) : (
            // Empty Image Placeholder
            <div
              className={cn("w-full bg-base-200 h-[184px] rounded-t-2xl", {
                "object-cover": imgFit === "cover",
                "object-contain": imgFit === "contain",
                "max-h-28": size === "small",
                "rounded-t-lg": size === "small" || size === "xlarge",
              })}
            ></div>
          )}

          {/* Card Content */}
          {children}
        </div>
      </div>

      {/* Winner Badge */}
      {winner && <div className="absolute top-4 right-4 bg-pink-500 text-white shadow-md px-2 py-1 rounded-lg text-sm font-medium">Top score</div>}
    </div>
  );
};
